import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";

function DsToOutletBilling() {
  const [userProfile, setUserProfile] = useState(null);
  const [dsList, setDsList] = useState([]);
  const [outletList, setOutletList] = useState([]);
  const [skuMaster, setSkuMaster] = useState({});
  const [skuList, setSkuList] = useState([]);
  const [items, setItems] = useState({});
  const [selectedDs, setSelectedDs] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [search, setSearch] = useState("");
  const [stockLevels, setStockLevels] = useState({});
  const [distributorId, setDistributorId] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchingOutlets, setFetchingOutlets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/"); return; }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setUserProfile(profile);
        loadData(profile);
      }
    });
    return () => unsub();
  }, [navigate]);

  const loadData = async (profile) => {
    setLoading(true);
    try {
      // 1. Load DS List
      let dsQ;
      if (["admin", "superadmin"].includes(profile.role)) {
          dsQ = query(collection(db, "dsMaster"));
      } else {
          dsQ = query(collection(db, "dsMaster"), where("distributorId", "==", profile.partyId || ""));
      }
      const dsSnap = await getDocs(dsQ);
      const dsArr = dsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDsList(dsArr);

      // 2. Load SKU Master
      const skuQ = query(collection(db, "skuMaster"));
      const skuSnap = await getDocs(skuQ);
      const smap = {};
      const slist = [];
      const itemMap = {};
      skuSnap.forEach(d => {
        const data = d.data();
        const skuKey = String(data.sku || data.Name || "").trim().toUpperCase();
        smap[skuKey] = data;
        slist.push(skuKey);
        itemMap[skuKey] = { bag: "", ctn: "", pkt: "" };
      });

      // Local sort to avoid index requirement
      slist.sort((a,b) => (Number(smap[a].sortorder) || 999) - (Number(smap[b].sortorder) || 999));

      setSkuMaster(smap);
      setSkuList(slist);
      setItems(itemMap);

      setDistributorId(profile.partyId || "");

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
     if (!selectedDs) {
         setOutletList([]);
         setStockLevels({});
         return;
     }

     const fetchDsSpecificData = async () => {
         setFetchingOutlets(true);
         try {
             // 1. Load Outlets for this DS
             const outletQ = query(collection(db, "outletMaster"), where("dsId", "==", selectedDs));
             const outletSnap = await getDocs(outletQ);
             setOutletList(outletSnap.docs.map(d => ({ id: d.id, ...d.data() })));

             // 2. Load Stock for this DS
             const todayStr = getISTDate();
             const stockRef = doc(db, "ds_stock_daily", `${selectedDs}_${todayStr}`);
             const stockSnap = await getDoc(stockRef);
             if (stockSnap.exists()) {
                 const inv = stockSnap.data().inventory || {};
                 const levels = {};
                 Object.entries(inv).forEach(([sku, data]) => {
                     const skuUpper = sku.trim().toUpperCase();
                     levels[skuUpper] = (levels[skuUpper] || 0) + (data.opening || 0) + (data.billed || 0) - (data.sold || 0);
                 });
                 setStockLevels(levels);
             } else {
                 setStockLevels({});
             }
         } catch (err) {
             console.error(err);
         } finally {
             setFetchingOutlets(false);
         }
     };

     fetchDsSpecificData();
  }, [selectedDs]);

  const totals = useMemo(() => {
    let tQty = 0, tAmt = 0;
    Object.entries(items).forEach(([sku, units]) => {
      const m = skuMaster[sku];
      if (!m) return;
      
      const c_Bag = Number(m.cartonsPerBag) || 0;
      const c_Ctn = Number(m.packetsPerCarton) || 0;

      const totalPkt = (Number(units.bag || 0) * (c_Bag || 1) * (c_Ctn || 1)) + 
                       (Number(units.ctn || 0) * (c_Ctn || 1)) + 
                       Number(units.pkt || 0);

      if (totalPkt <= 0) return;
      
      const rate = Number(m.Basic_Eway || 0); // Retail/Outlet Basic Rate
      const basic = totalPkt * rate;
      const gstRate = Number(m.gst) || 5;
      const gst = basic * (gstRate / 100);
      const cess = basic * (Number(m.cess || 0) / 100);
      const addl = totalPkt * (Number(m.addlcess || 0));
      const rebate = totalPkt * (Number(m.Rebate || 0));

      tQty += totalPkt;
      tAmt += (basic + gst + cess + addl + rebate);
    });
    return { tQty, tAmt };
  }, [items, skuMaster]);

  const handleSaveOrder = async () => {
    if (!selectedOutlet) { alert("Please select an Outlet"); return; }
    if (totals.tQty === 0) { alert("Please enter quantities"); return; }
    if (submitting) return;

    setSubmitting(true);
    try {
      const cleanItems = {};
      Object.entries(items).forEach(([sku, units]) => {
        const skuUpper = String(sku).trim().toUpperCase();
        const m = skuMaster[skuUpper];
        const c_Bag = Number(m.cartonsPerBag) || 1;
        const c_Ctn = Number(m.packetsPerCarton) || 1;
        const totalPkt = (Number(units.bag || 0) * c_Bag * c_Ctn) + 
                         (Number(units.ctn || 0) * c_Ctn) + 
                         Number(units.pkt || 0);
        if (totalPkt > 0) cleanItems[skuUpper] = totalPkt;
      });

      const outletObj = outletList.find(o => o.outletId === selectedOutlet);

      const docRef = await addDoc(collection(db, "orders"), {
        clientId: userProfile.clientId,
        distributorId: distributorId || "",
        dsId: selectedDs,
        partyId: selectedOutlet,
        partyName: outletObj?.name || selectedOutlet,
        items: cleanItems,
        totalQty: totals.tQty,
        totalAmount: totals.tAmt,
        source: "DS_TO_OUTLET",
        status: "PENDING",
        createdAt: serverTimestamp(),
      });

      alert("Billing Saved! Redirecting to Approval...");
      navigate(`/approve-order/${docRef.id}`);
    } catch (err) {
      console.error(err);
      alert("Error saving: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Layout>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <div style={{ fontSize: "20px", color: "#4a5568", fontWeight: "600" }}>Loading Salesman Billing Interface...</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER SECTION */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Direct Sales Matrix</h1>
            <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Layer 2: Real-time billing from DS cache to retail outlets. Authorized distribution mode.</p>
          </div>
          <div style={{ display: "flex", gap: 30 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{totals.tQty}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Load Units</div>
            </div>
            <div style={{ width: 1, height: 40, background: "var(--border)" }}></div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "var(--success)" }}>₹{totals.tAmt.toLocaleString()}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Gross Value</div>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 32 }}>
          {/* MAIN BILLING INTERFACE */}
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            
            {/* DESTINATION SELECTION */}
            <div className="premium-card" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "center" }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Select Salesman (DS)</label>
                <select 
                  className="form-input"
                  value={selectedDs} 
                  onChange={e => { setSelectedDs(e.target.value); setSelectedOutlet(""); }} 
                  style={{ width: "100%", padding: 14 }}
                >
                  <option value="">-- Choose Salesman --</option>
                  {dsList.map(ds => (
                    <option key={ds.dsId} value={ds.dsId}>
                      {ds.name} ({ds.dsId})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Destination Outlet</label>
                <select 
                  className="form-input"
                  value={selectedOutlet} 
                  onChange={e => setSelectedOutlet(e.target.value)} 
                  disabled={!selectedDs || fetchingOutlets}
                  style={{ width: "100%", padding: 14 }}
                >
                  <option value="">
                    {fetchingOutlets ? "Loading Outlets..." : selectedDs ? "-- Choose Outlet from Registry --" : "Select DS first"}
                  </option>
                  {outletList.map(o => (
                    <option key={o.outletId} value={o.outletId}>
                      {o.name} • {o.outletId} ({o.beat || "Global"})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="premium-card">
                <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Search Manifest</label>
                <input 
                  className="form-input" 
                  placeholder="Query SKU by name or code..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  disabled={!selectedOutlet}
                  style={{ width: "100%", padding: 14 }}
                />
            </div>

            {/* PRODUCT MATRIX */}
            <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-main)" }}>DS CARGO MANIFEST</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>TARGET: OUTLET RETAIL TIER</span>
              </div>
              <div style={{ maxHeight: 600, overflowY: "auto" }}>
                <table className="enterprise-table">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>Identity</th>
                      <th>Product Blueprint</th>
                      <th style={{ textAlign: "center" }}>Live Stock</th>
                      <th style={{ width: 220, textAlign: "center" }}>Provision</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedOutlet ? (
                        <tr>
                            <td colSpan="5" style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>
                                <div style={{ fontSize: 40, marginBottom: 12 }}>🏘️</div>
                                <div style={{ fontWeight: 700 }}>Select a Destination Outlet to Load Manifest</div>
                                <div style={{ fontSize: 12 }}>SKU matrix will flow automatically upon registry lock.</div>
                            </td>
                        </tr>
                    ) : skuList
                      .filter(s => s.toLowerCase().includes(search.toLowerCase()) || 
                                  skuMaster[s]?.Name?.toLowerCase().includes(search.toLowerCase()))
                      .map(sku => {
                        const m = skuMaster[sku];
                        const rate = Number(m.Basic_Eway || 0);
                        const units = items[sku] || { bag: "", ctn: "", pkt: "" };
                        
                        const c_Bag = Number(m.cartonsPerBag) || 1;
                        const c_Ctn = Number(m.packetsPerCarton) || 1;
                        const totalPkt = (Number(units.bag || 0) * c_Bag * c_Ctn) + 
                                         (Number(units.ctn || 0) * c_Ctn) + 
                                         Number(units.pkt || 0);

                        const sub = totalPkt * rate;
                        const stock = stockLevels[sku] || 0;
                        const isLow = stock <= 0;
                        const isOverStock = totalPkt > stock;

                        return (
                          <tr key={sku}>
                            <td style={{ fontWeight: 800, color: "var(--primary)", fontSize: 12 }}>{sku}</td>
                            <td>
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{m.Name}</div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>MRP: ₹{m.mrp} | {m.Brand || "Core Portfolio"}</div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span style={{ 
                                padding: "4px 10px", 
                                borderRadius: 8, 
                                fontSize: 11, 
                                fontWeight: 800,
                                background: isLow ? "rgba(239, 68, 68, 0.1)" : "rgba(34, 197, 94, 0.1)",
                                color: isLow ? "var(--error)" : "var(--success)"
                              }}>
                                {stock} units
                              </span>
                            </td>
                            <td style={{ textAlign: "center" }}>
                                <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                    <div>
                                        <div style={{ fontSize: 9, color: "var(--text-muted)" }}>BAG</div>
                                        <input 
                                            type="number" 
                                            className="form-input"
                                            value={units.bag} 
                                            onChange={e => setItems({ ...items, [sku]: { ...units, bag: e.target.value } })}
                                            style={{ width: 45, textAlign: "center", fontWeight: 800, padding: 6, fontSize: 13, borderColor: isOverStock ? "var(--error)" : "var(--border)" }}
                                        />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, color: "var(--text-muted)" }}>CTN</div>
                                        <input 
                                            type="number" 
                                            className="form-input"
                                            value={units.ctn} 
                                            onChange={e => setItems({ ...items, [sku]: { ...units, ctn: e.target.value } })}
                                            style={{ width: 45, textAlign: "center", fontWeight: 800, padding: 6, fontSize: 13, borderColor: isOverStock ? "var(--error)" : "var(--border)" }}
                                        />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, color: "var(--text-muted)" }}>PKT</div>
                                        <input 
                                            type="number" 
                                            className="form-input"
                                            value={units.pkt} 
                                            onChange={e => setItems({ ...items, [sku]: { ...units, pkt: e.target.value } })}
                                            style={{ width: 45, textAlign: "center", fontWeight: 800, padding: 6, fontSize: 13, borderColor: isOverStock ? "var(--error)" : "var(--border)" }}
                                        />
                                    </div>
                                </div>
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 800, color: "var(--text-main)" }}>₹{sub.toFixed(2)}</td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* SIDEBAR: SUMMARY & AUTHORIZATION */}
          <div style={{ position: "sticky", top: 100 }}>
            <div className="premium-card" style={{ background: "var(--text-main)", color: "white", padding: 28 }}>
              <h3 style={{ margin: "0 0 24px 0", fontSize: 13, fontWeight: 800, letterSpacing: "0.05em", opacity: 0.6 }}>TRANSACTION SUMMARY</h3>
              
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ opacity: 0.7 }}>Retail Basic</span>
                  <span style={{ fontWeight: 700 }}>₹{totals.tAmt.toFixed(2)}</span>
                </div>
                
                <div style={{ width: "100%", height: 1, background: "rgba(255,255,255,0.1)", margin: "8px 0" }}></div>
                
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.7 }}>SHIPMENT VALUE</span>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: "var(--success)" }}>₹{totals.tAmt.toFixed(2)}</div>
                    <div style={{ fontSize: 10, opacity: 0.5 }}>TAX INCLUSIVE (NET)</div>
                  </div>
                </div>

                <button 
                    onClick={handleSaveOrder}
                    disabled={submitting || totals.tQty === 0}
                    className="btn-primary"
                    style={{ 
                        width: "100%", 
                        padding: 18, 
                        marginTop: 12, 
                        fontSize: 16, 
                        background: "var(--success)",
                        border: "none",
                        color: "white"
                    }}
                >
                    {submitting ? "SYNCING LOAD..." : "FINALIZE & BILL OUTLET"}
                </button>
                
                <div style={{ textAlign: "center", fontSize: 11, opacity: 0.5 }}>
                  This action deducts stock from DS live inventory and generates a retail invoice.
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: 20, padding: 20, borderRadius: 20, background: "rgba(0,0,0,0.03)", border: "1px dashed var(--border)" }}>
               <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>DISTRIBUTION GUARD</div>
               <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
                 Ensure all quantities match the physical load. Discrepancies will affect your end-of-day reconciliation.
               </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default DsToOutletBilling;

